import { zoneForHeight, type StackTowerZonePalette } from '@ranked-waiting/stack-tower-core';
import { worldToScreenY } from './stack-tower-camera';

/**
 * Phase 5 scene: a procedurally drawn zone background (sky gradient +
 * parallax starfield/haze/skyline) that shifts colour by altitude, and a
 * smooth cross-fade between adjacent zones at their boundaries. No sprite or
 * image assets — everything is gradients/shapes so v1's bundle growth stays
 * code-only (spec §2 of the plan).
 *
 * Design choice worth noting: scenery *layers* are driven by continuous
 * camera altitude (skyline near the ground fading out, haze in the middle,
 * stars fading in up high), while *colour* is driven by the zone palette.
 * That's deliberately simpler and more robust than swapping discrete
 * per-zone scenery: the zones read as colour + label milestones over one
 * continuous parallax field, with nothing to pop or mis-align at a boundary.
 */

/** Blocks of overlap on each side of a zone boundary over which the palette cross-fades. */
const TRANSITION_BAND = 4;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linear interpolation between two `#rrggbb` colours, returned as `rgb(...)`. */
function lerpHex(hex1: string, hex2: string, t: number): string {
  const a = parseInt(hex1.slice(1), 16);
  const b = parseInt(hex2.slice(1), 16);
  const r = Math.round(lerp((a >> 16) & 255, (b >> 16) & 255, t));
  const g = Math.round(lerp((a >> 8) & 255, (b >> 8) & 255, t));
  const bl = Math.round(lerp(a & 255, b & 255, t));
  return `rgb(${r},${g},${bl})`;
}

/**
 * The zone palette for `climbHeight` (blocks climbed since the last reset),
 * cross-faded toward the neighbouring zone within `TRANSITION_BAND` blocks
 * of a boundary (exactly 50/50 at the boundary itself, so the two sides meet
 * continuously). Only the two sky colours and the accent blend; the subtle
 * `haze` just takes the dominant zone's value — a hard swap there is
 * imperceptible next to the sky gradient.
 */
export function blendedZonePalette(climbHeight: number): StackTowerZonePalette {
  const zone = zoneForHeight(climbHeight);
  let other: StackTowerZonePalette | null = null;
  let t = 0;

  if (zone.maxHeight !== null && zone.maxHeight - climbHeight < TRANSITION_BAND) {
    other = zoneForHeight(zone.maxHeight + 1).palette;
    t = (TRANSITION_BAND - (zone.maxHeight - climbHeight)) / (2 * TRANSITION_BAND); // → 0.5 at the boundary
  } else {
    const distFromBottom = climbHeight - zone.minHeight;
    if (zone.minHeight > 0 && distFromBottom < TRANSITION_BAND) {
      other = zoneForHeight(zone.minHeight - 1).palette;
      t = (TRANSITION_BAND - distFromBottom) / (2 * TRANSITION_BAND);
    }
  }

  if (!other) return zone.palette;
  return {
    skyTop: lerpHex(zone.palette.skyTop, other.skyTop, t),
    skyBottom: lerpHex(zone.palette.skyBottom, other.skyBottom, t),
    accent: lerpHex(zone.palette.accent, other.accent, t),
    haze: t < 0.5 ? zone.palette.haze : other.haze,
  };
}

// --- deterministic scenery (generated once, so nothing flickers frame to frame) ---

/** mulberry32 — a tiny deterministic PRNG, so the starfield/skyline are stable across frames and runs. */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let x = Math.imul(a ^ (a >>> 15), 1 | a);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

interface Star {
  readonly xFrac: number;
  readonly yFrac: number;
  readonly r: number;
}
interface Building {
  readonly xFrac: number;
  readonly wFrac: number;
  readonly hFrac: number;
}
interface Cloud {
  readonly xFrac: number;
  readonly yFrac: number;
  readonly rxFrac: number;
}

const STARS: Star[] = (() => {
  const rnd = mulberry32(1337);
  return Array.from({ length: 90 }, () => ({ xFrac: rnd(), yFrac: rnd(), r: 0.4 + rnd() * 1.3 }));
})();

const BUILDINGS: Building[] = (() => {
  const rnd = mulberry32(4242);
  const out: Building[] = [];
  let x = -0.05;
  while (x < 1.05) {
    const wFrac = 0.04 + rnd() * 0.06;
    out.push({ xFrac: x, wFrac, hFrac: 0.08 + rnd() * 0.16 });
    x += wFrac + rnd() * 0.015;
  }
  return out;
})();

const CLOUDS: Cloud[] = (() => {
  const rnd = mulberry32(99);
  return Array.from({ length: 7 }, () => ({ xFrac: rnd(), yFrac: rnd(), rxFrac: 0.12 + rnd() * 0.16 }));
})();

export interface BackgroundFrame {
  readonly ctx: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;
  readonly cameraOffset: number;
  /** Blocks climbed since the last reset. Named distinctly from this frame's own canvas-pixel `height` above. */
  readonly climbHeight: number;
}

export function drawStackTowerBackground(frame: BackgroundFrame): void {
  const { ctx, width, height, cameraOffset, climbHeight } = frame;
  const palette = blendedZonePalette(climbHeight);

  // Sky gradient — the dominant zone signal.
  const sky = ctx.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, palette.skyTop);
  sky.addColorStop(1, palette.skyBottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, height);

  // Stars — fade in as the camera climbs past ~half a screen, drift very slowly (distant parallax).
  const starAlpha = clamp01((cameraOffset - height * 0.4) / (height * 1.5));
  if (starAlpha > 0) {
    ctx.fillStyle = palette.accent;
    for (const s of STARS) {
      const y = (s.yFrac * height + cameraOffset * 0.05) % height;
      ctx.globalAlpha = starAlpha * (0.5 + s.r / 2);
      ctx.beginPath();
      ctx.arc(s.xFrac * width, y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // Haze clouds — a slow mid-parallax field, present at every altitude for a sense of motion.
  ctx.fillStyle = palette.haze;
  for (const c of CLOUDS) {
    const bandH = height * 1.4;
    const y = ((c.yFrac * bandH + cameraOffset * 0.35) % bandH) - height * 0.2;
    const rx = c.rxFrac * width;
    ctx.beginPath();
    ctx.ellipse(c.xFrac * width, y, rx, rx * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Ground skyline — dark silhouettes anchored near the ground, scrolling away
  // (slightly slower than the tower) and fading out as the city drops below.
  const groundScreenY = worldToScreenY(0, cameraOffset * 0.85, height);
  const skylineAlpha = clamp01(1 - cameraOffset / (height * 0.9));
  if (skylineAlpha > 0 && groundScreenY > -height) {
    ctx.save();
    ctx.globalAlpha = skylineAlpha;
    ctx.fillStyle = 'rgba(8,8,14,0.72)';
    for (const b of BUILDINGS) {
      const h = b.hFrac * height;
      ctx.fillRect(b.xFrac * width, groundScreenY - h, b.wFrac * width, h + height);
    }
    ctx.restore();
  }
}
